import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyLogo } from '../hooks/useCompanyLogo';
import {
  ArrowLeftIcon, PencilSquareIcon, QrCodeIcon, DocumentTextIcon, TrashIcon,
  PrinterIcon, CalendarDaysIcon, PlusIcon, CheckIcon, ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DatePicker } from '../components/ui/date-picker';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';
import AssetFormModal from '../components/AssetFormModal';
import { Asset, Maint, AREA_LABEL, ESTADOS, ESTADO_LABEL, ESTADO_BADGE, pad2, today } from './assetsShared';

// Tipos de rutina/mantenimiento precargados (+ opción "Otro" para escribir)
const RUTINAS = [
  'Limpieza general',
  'Limpieza de filtro',
  'Service / mantenimiento preventivo',
  'Lubricación',
  'Cambio de repuesto / pieza',
  'Calibración / ajuste',
  'Revisión / inspección',
  'Reparación',
];

const RutinaSelect: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [otro, setOtro] = useState(() => value !== '' && !RUTINAS.includes(value));
  const selectValue = otro ? '__otro__' : (RUTINAS.includes(value) ? value : '');
  return (
    <div className="space-y-1.5">
      <Select
        value={selectValue}
        onValueChange={v => { if (v === '__otro__') { setOtro(true); onChange(''); } else { setOtro(false); onChange(v); } }}
      >
        <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
        <SelectContent>
          {RUTINAS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          <SelectItem value="__otro__">Otro (especificar)</SelectItem>
        </SelectContent>
      </Select>
      {otro && <Input value={value} onChange={e => onChange(e.target.value)} placeholder="Especificar rutina / tipo..." />}
    </div>
  );
};

// Selector múltiple de responsables (guarda los nombres separados por coma)
const ResponsableSelect: React.FC<{ value: string; onChange: (v: string) => void; users: { id: string; name: string }[] }> = ({ value, onChange, users }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
  const toggle = (name: string) => {
    const set = new Set(selected);
    if (set.has(name)) set.delete(name); else set.add(name);
    onChange(Array.from(set).join(', '));
  };
  const filtered = users.filter(u => u.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover open={open} onOpenChange={o => { setOpen(o); if (!o) setQ(''); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between font-normal h-9">
          <span className={`truncate ${selected.length ? '' : 'text-muted-foreground'}`}>
            {selected.length ? selected.join(', ') : 'Seleccionar responsable(s)'}
          </span>
          <ChevronUpDownIcon className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="p-2 border-b border-border">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar..." className="h-8" />
        </div>
        <div className="max-h-60 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Sin usuarios</div>
          ) : filtered.map(u => (
            <button
              type="button"
              key={u.id}
              onClick={() => toggle(u.name)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-accent"
            >
              <CheckIcon className={`h-4 w-4 shrink-0 ${selected.includes(u.name) ? 'opacity-100' : 'opacity-0'}`} />
              {u.name}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const AssetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { getLogoUrl } = useCompanyLogo();
  const canManage = hasPermission('manage_assets') || hasPermission('manage_settings');

  const [asset, setAsset] = useState<Asset | null>(null);
  const [events, setEvents] = useState<Maint[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);

  // Etiqueta
  const [labelOpen, setLabelOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState('');

  // Form de evento
  const [mode, setMode] = useState<'mantenimiento' | 'incidencia'>('mantenimiento');
  const [evForm, setEvForm] = useState({ fecha: today(), rutina: '', responsable: '', observaciones: '', nuevoEstado: 'en_reparacion' });
  const [progForm, setProgForm] = useState({ fechaProgramada: today(), rutina: '', observaciones: '', recurrente: true, frecuenciaValor: '3', frecuenciaUnidad: 'meses' });
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => { axios.get('/api/assets/users-list').then(r => setUsers(r.data.users || [])).catch(() => {}); }, []);
  const [saving, setSaving] = useState(false);
  const [showMaintForm, setShowMaintForm] = useState(false);
  const [showProgForm, setShowProgForm] = useState(false);
  const [editProgId, setEditProgId] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await axios.get(`/api/assets/${id}`);
      setAsset(r.data.asset); setEvents(r.data.maintenance || []);
    } catch { setNotFound(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submitEvent = async () => {
    if (!asset) return;
    setSaving(true);
    try {
      const body: any = { tipo: mode, fecha: evForm.fecha, responsable: evForm.responsable, observaciones: evForm.observaciones };
      if (mode === 'incidencia') body.nuevoEstado = evForm.nuevoEstado; else body.rutina = evForm.rutina;
      await axios.post(`/api/assets/${asset.id}/event`, body);
      toast.success(mode === 'incidencia' ? 'Incidencia registrada' : 'Mantenimiento registrado');
      if (mode === 'incidencia') setAsset(a => a ? { ...a, estado: evForm.nuevoEstado } : a);
      setEvForm(f => ({ ...f, observaciones: '' }));
      setShowMaintForm(false);
      await load();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Error al registrar'); }
    finally { setSaving(false); }
  };

  const openProgNew = () => {
    setEditProgId(null);
    setProgForm({ fechaProgramada: today(), rutina: '', observaciones: '', recurrente: true, frecuenciaValor: '3', frecuenciaUnidad: 'meses' });
    setShowProgForm(true);
  };
  const openProgEdit = (ev: Maint) => {
    setEditProgId(ev.id);
    setProgForm({
      fechaProgramada: ev.fechaProgramada || today(), rutina: ev.rutina || '', observaciones: ev.observaciones || '',
      recurrente: !!ev.frecuenciaValor, frecuenciaValor: String(ev.frecuenciaValor || 3), frecuenciaUnidad: ev.frecuenciaUnidad || 'meses',
    });
    setShowProgForm(true);
  };
  const submitProg = async () => {
    if (!asset) return;
    if (!progForm.fechaProgramada) { toast.error('Elegí la fecha de la revisión'); return; }
    setSaving(true);
    try {
      const body: any = { fechaProgramada: progForm.fechaProgramada, rutina: progForm.rutina, observaciones: progForm.observaciones };
      body.frecuenciaValor = progForm.recurrente ? Number(progForm.frecuenciaValor) : null;
      body.frecuenciaUnidad = progForm.recurrente ? progForm.frecuenciaUnidad : null;
      if (editProgId) {
        await axios.put(`/api/assets/${asset.id}/scheduled/${editProgId}`, body);
        toast.success('Revisión actualizada');
      } else {
        await axios.post(`/api/assets/${asset.id}/event`, { tipo: 'programada', ...body });
        toast.success('Revisión programada');
      }
      setEditProgId(null);
      setShowProgForm(false);
      await load();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };
  const deleteProg = async (ev: Maint) => {
    if (!(await confirmDialog({ title: 'Eliminar revisión', description: `¿Eliminar la revisión programada del ${ev.fechaProgramada}?`, confirmText: 'Eliminar' }))) return;
    try { await axios.delete(`/api/assets/maintenance/${ev.id}`); toast.success('Revisión eliminada'); await load(); }
    catch { toast.error('Error al eliminar'); }
  };

  const deleteMaint = async (ev: Maint) => {
    if (!(await confirmDialog({ title: 'Eliminar registro', description: '¿Eliminar este registro de mantenimiento / incidencia? Esta acción no se puede deshacer.', confirmText: 'Eliminar' }))) return;
    try { await axios.delete(`/api/assets/maintenance/${ev.id}`); toast.success('Registro eliminado'); await load(); }
    catch { toast.error('Error al eliminar'); }
  };

  const markDone = async (ev: Maint) => {
    if (!asset) return;
    try { await axios.post(`/api/assets/${asset.id}/scheduled/${ev.id}/done`, { fecha: today() }); toast.success('Revisión realizada'); await load(); }
    catch { toast.error('Error'); }
  };

  const openLabel = async () => {
    if (!asset) return;
    try { setQrUrl(await QRCode.toDataURL(`${window.location.origin}/activo/${asset.qrToken}`, { width: 300, margin: 1 })); } catch { setQrUrl(''); }
    setLabelOpen(true);
  };
  const printLabel = async () => {
    if (!asset) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const toData = async (url: string) => {
      try {
        const r = await fetch(url); const b = await r.blob();
        return await new Promise<string>(res => { const fr = new FileReader(); fr.onload = () => res(String(fr.result)); fr.onerror = () => res(''); fr.readAsDataURL(b); });
      } catch { return ''; }
    };
    let logoData = await toData(getLogoUrl());
    if (!logoData) logoData = await toData('/rocam-new-logo-2025.png');
    const desc = `${asset.description}${asset.modelo ? ' · ' + asset.modelo : ''}`;
    const meta = `Sector: ${AREA_LABEL[asset.area] || asset.area}${asset.fechaAlta ? ' · Alta: ' + asset.fechaAlta : ''}`;
    w.document.write(`<html><head><title>${asset.code}</title>
      <style id="lblcss">
        .lbl { width: 150mm; border: 3px solid #111; border-radius: 12px; padding: 8mm; display: flex; gap: 6mm; align-items: center; background: #fff; color: #111; box-sizing: border-box; font-family: Arial, sans-serif; }
        .lbl * { box-sizing: border-box; }
        .lbl .info { flex: 1; min-width: 0; } .lbl .logo { height: 10mm; margin-bottom: 2mm; display: block; max-width: 100%; object-fit: contain; }
        .lbl .code { font-family: monospace; font-weight: 800; font-size: 32px; margin: 1mm 0; color: #111; } .lbl .desc { font-size: 18px; line-height: 1.25; color: #111; }
        .lbl .meta { font-size: 13px; color: #555; margin-top: 3mm; } .lbl .qr { width: 46mm; height: 46mm; flex-shrink: 0; }
      </style>
      <style>
        @page { margin: 0; }
        html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; background: #f3f3f3; }
        .bar { position: sticky; top: 0; z-index: 10; display: flex; justify-content: flex-end; gap: 10px; padding: 10px 16px; background: #111; border-bottom: 1px solid #333; }
        .bar button { font: 600 14px Arial; padding: 9px 18px; border-radius: 8px; border: 1px solid #444; background: #222; color: #fff; cursor: pointer; }
        .bar button.primary { background: #2563eb; border-color: #2563eb; }
        .page { display: flex; align-items: center; justify-content: center; min-height: calc(100vh - 58px); padding: 20px; }
        @media print { .bar { display: none; } .page { min-height: 100vh; padding: 0; } body { background: #fff; } }
      </style></head>
      <body>
        <div class="bar">
          <button class="primary" onclick="dl()">Descargar</button>
          <button onclick="window.print()">Imprimir</button>
        </div>
        <div class="page"><div class="lbl"><div class="info">
          ${logoData ? `<img class="logo" src="${logoData}" />` : ''}<div class="code">${asset.code}</div>
          <div class="desc">${desc}</div>
          <div class="meta">${meta}</div>
        </div><img class="qr" src="${qrUrl}" /></div></div>
        <script>
          function dl(){
            var el=document.querySelector('.lbl');
            var r=el.getBoundingClientRect();
            var w=Math.ceil(r.width), h=Math.ceil(r.height);
            var css=document.getElementById('lblcss').textContent;
            var xml=new XMLSerializer().serializeToString(el);
            var svg='<svg xmlns="http://www.w3.org/2000/svg" width="'+w+'" height="'+h+'"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml"><style>'+css+'</style>'+xml+'</div></foreignObject></svg>';
            var img=new Image();
            img.onload=function(){
              var s=3, c=document.createElement('canvas'); c.width=w*s; c.height=h*s;
              var x=c.getContext('2d'); x.scale(s,s); x.drawImage(img,0,0);
              var a=document.createElement('a'); a.download='${asset.code}.png'; a.href=c.toDataURL('image/png'); a.click();
            };
            img.src='data:image/svg+xml;charset=utf-8,'+encodeURIComponent(svg);
          }
        <\/script>
      </body></html>`);
    w.document.close();
  };

  const remove = async () => {
    if (!asset) return;
    if (!(await confirmDialog({ title: 'Eliminar equipo', description: `¿Eliminar ${asset.code}? No se puede deshacer.`, confirmText: 'Eliminar' }))) return;
    try { await axios.delete(`/api/assets/${asset.id}`); toast.success('Eliminado'); navigate('/assets'); }
    catch { toast.error('Error al eliminar'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>;
  if (notFound || !asset) return <div className="py-20 text-center text-muted-foreground">Equipo no encontrado. <button className="text-primary underline" onClick={() => navigate('/assets')}>Volver</button></div>;

  const programadas = events.filter(e => e.tipo === 'programada' && !e.completado);
  const realizados = events.filter(e => e.tipo === 'mantenimiento' || e.tipo === 'incidencia');
  const tokenParam = `token=${localStorage.getItem('token') || ''}`;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <button onClick={() => navigate('/assets')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground w-fit">
          <ArrowLeftIcon className="h-4 w-4" /> Equipos y herramientas
        </button>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-2xl font-bold tracking-tight font-mono">{asset.code}</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ESTADO_BADGE[asset.estado]}`}>{ESTADO_LABEL[asset.estado]}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{asset.description}{(asset.marca || asset.modelo) ? ` · ${[asset.marca, asset.modelo].filter(Boolean).join(' ')}` : ''}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {asset.fichaDocId && <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.open(`/api/manuals/${asset.fichaDocId}/file?${tokenParam}`, '_blank')}><DocumentTextIcon className="h-4 w-4" /> Ficha técnica</Button>}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={openLabel}><QrCodeIcon className="h-4 w-4" /> Etiqueta / QR</Button>
            {canManage && <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditing(true)}><PencilSquareIcon className="h-4 w-4" /> Editar</Button>}
            {canManage && <Button variant="outline" size="sm" className="gap-1.5 text-red-600 hover:text-red-700" onClick={remove}><TrashIcon className="h-4 w-4" /> Eliminar</Button>}
          </div>
        </div>
      </div>

      <Tabs defaultValue="detalle">
        <TabsList>
          <TabsTrigger value="detalle">Detalle</TabsTrigger>
          <TabsTrigger value="mantenimiento">Mantenimiento</TabsTrigger>
          <TabsTrigger value="programacion">Programación{programadas.length > 0 ? ` (${programadas.length})` : ''}</TabsTrigger>
        </TabsList>

        {/* DETALLE */}
        <TabsContent value="detalle" className="mt-4">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2"><CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="secondary" className="text-xs">{asset.clase === 'EQ' ? 'Equipo' : 'Herramienta'}</Badge>
                <Badge variant="secondary" className="text-xs">{AREA_LABEL[asset.area] || asset.area}</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                {asset.marca && <div><div className="text-xs text-muted-foreground">Marca</div>{asset.marca}</div>}
                {asset.modelo && <div><div className="text-xs text-muted-foreground">Modelo</div>{asset.modelo}</div>}
                {asset.ubicacionFisica && <div><div className="text-xs text-muted-foreground">Ubicación</div>{asset.ubicacionFisica}</div>}
                {asset.responsable && <div><div className="text-xs text-muted-foreground">Responsable</div>{asset.responsable}</div>}
                {asset.fechaAlta && <div><div className="text-xs text-muted-foreground">Alta</div>{asset.fechaAlta}</div>}
                {asset.nroSerie && <div><div className="text-xs text-muted-foreground">N° serie</div>{asset.nroSerie}</div>}
                {asset.proveedor && <div><div className="text-xs text-muted-foreground">Proveedor</div>{asset.proveedor}</div>}
                {asset.fechaCompra && <div><div className="text-xs text-muted-foreground">Compra</div>{asset.fechaCompra}</div>}
                {asset.garantia && <div><div className="text-xs text-muted-foreground">Garantía</div>{asset.garantia}</div>}
              </div>
              {asset.specs && asset.specs.length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-1">Ficha técnica</div>
                  <div className="divide-y divide-border border border-border rounded-lg">
                    {asset.specs.map((s, i) => <div key={i} className="flex justify-between gap-3 px-3 py-1.5 text-sm"><span className="text-muted-foreground">{s.label}</span><span className="font-medium text-right">{s.value}</span></div>)}
                  </div>
                </div>
              )}
              {asset.notes && <div className="text-sm"><div className="text-xs text-muted-foreground">Notas</div>{asset.notes}</div>}
            </CardContent></Card>
            <Card><CardContent className="p-4">
              {asset.fotoDriveId ? (
                <img src={`/api/assets/public/${asset.qrToken}/photo`} alt="" className="w-full rounded-lg object-contain max-h-72 bg-muted" />
              ) : (
                <div className="text-sm text-muted-foreground text-center py-10">Sin foto.<br />Subila editando el equipo o por el QR.</div>
              )}
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* MANTENIMIENTO */}
        <TabsContent value="mantenimiento" className="mt-4">
          {!showMaintForm ? (
            <div className="flex justify-end">
              <Button onClick={() => setShowMaintForm(true)} className="gap-1.5"><PlusIcon className="h-4 w-4" /> Registrar mantenimiento o incidencia</Button>
            </div>
          ) : (
          <Card><CardContent className="p-4 space-y-3 max-w-xl">
            <div className="inline-flex rounded-lg border border-border overflow-hidden text-sm">
              {(['mantenimiento', 'incidencia'] as const).map((m, i) => (
                <button key={m} type="button" onClick={() => setMode(m)} className={`px-3 py-1.5 ${i > 0 ? 'border-l border-border' : ''} ${mode === m ? 'bg-foreground text-background' : 'bg-background hover:bg-muted'}`}>
                  {m === 'mantenimiento' ? 'Mantenimiento' : 'Incidencia'}
                </button>
              ))}
            </div>
            {mode === 'incidencia' ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Fecha</Label><DatePicker value={evForm.fecha} onChange={v => setEvForm(f => ({ ...f, fecha: v }))} /></div>
                  <div className="space-y-1"><Label className="text-xs">Nuevo estado</Label>
                    <Select value={evForm.nuevoEstado} onValueChange={v => setEvForm(f => ({ ...f, nuevoEstado: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{ESTADOS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Responsable</Label><ResponsableSelect value={evForm.responsable} onChange={v => setEvForm(f => ({ ...f, responsable: v }))} users={users} /></div>
                <div className="space-y-1"><Label className="text-xs">Descripción de la incidencia</Label><Input value={evForm.observaciones} onChange={e => setEvForm(f => ({ ...f, observaciones: e.target.value }))} placeholder="Qué pasó" /></div>
                <p className="text-[11px] text-muted-foreground">Cambia el estado del equipo a <b>{ESTADO_LABEL[evForm.nuevoEstado]}</b>.</p>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Fecha</Label><DatePicker value={evForm.fecha} onChange={v => setEvForm(f => ({ ...f, fecha: v }))} /></div>
                  <div className="space-y-1"><Label className="text-xs">Rutina / tipo</Label>
                    <RutinaSelect value={evForm.rutina} onChange={v => setEvForm(f => ({ ...f, rutina: v }))} />
                  </div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Responsable</Label><ResponsableSelect value={evForm.responsable} onChange={v => setEvForm(f => ({ ...f, responsable: v }))} users={users} /></div>
                <div className="space-y-1"><Label className="text-xs">Observaciones</Label><Input value={evForm.observaciones} onChange={e => setEvForm(f => ({ ...f, observaciones: e.target.value }))} placeholder="Qué se hizo / hallazgos" /></div>
              </>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={submitEvent} disabled={saving || (mode === 'incidencia' && !canManage)} className="flex-1">
                {saving ? 'Guardando...' : mode === 'incidencia' ? 'Registrar incidencia' : 'Registrar mantenimiento'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowMaintForm(false)}>Cancelar</Button>
            </div>
            {mode === 'incidencia' && !canManage && <p className="text-[11px] text-muted-foreground text-center">Las incidencias (cambio de estado) las registra gestión.</p>}
          </CardContent></Card>
          )}

          <Card><CardContent className="p-4">
            <div className="text-sm font-medium mb-2">Mantenimientos e incidencias registrados</div>
            {realizados.length === 0 ? <p className="text-sm text-muted-foreground">Todavía no hay mantenimientos ni incidencias registrados.</p> : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Fecha</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Tipo</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Responsable</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Observaciones</TableHead>
                      {canManage && <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right w-10">Acción</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {realizados.map(m => (
                      <TableRow key={m.id} className="border-b hover:bg-muted/50">
                        <TableCell className="p-2 align-middle font-medium whitespace-nowrap text-left">{m.fecha}</TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">{m.tipo === 'incidencia' ? 'Incidencia' : (m.rutina || 'Mantenimiento')}</TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          {m.tipo === 'incidencia' && m.nuevoEstado
                            ? <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ESTADO_BADGE[m.nuevoEstado]}`}>{ESTADO_LABEL[m.nuevoEstado]}</span>
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="p-2 align-middle text-left text-muted-foreground">{m.responsable || '—'}</TableCell>
                        <TableCell className="p-2 align-middle text-left text-muted-foreground">{m.observaciones || '—'}</TableCell>
                        {canManage && (
                          <TableCell className="p-2 align-middle text-right">
                            <Tip label="Eliminar"><Button size="icon" variant="ghost" className="size-7 text-muted-foreground hover:text-destructive" onClick={() => deleteMaint(m)}><TrashIcon className="h-4 w-4" /></Button></Tip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* PROGRAMACIÓN */}
        <TabsContent value="programacion" className="mt-4">
          <div className="space-y-4">
            {canManage && (
              <Button onClick={openProgNew} className="gap-1.5"><PlusIcon className="h-4 w-4" /> Programar revisión</Button>
            )}
            <Card><CardContent className="p-4">
              <div className="text-sm font-medium mb-2">Revisiones programadas</div>
              {programadas.length === 0 ? <p className="text-sm text-muted-foreground">No hay revisiones programadas.</p> : (
                <div className="space-y-1.5">
                  {programadas.map(m => { const vencida = (m.fechaProgramada || '') <= today(); return (
                    <div key={m.id} className={`text-sm border rounded p-2 flex items-center justify-between gap-2 ${vencida ? 'border-red-500/50 bg-red-500/5' : 'border-border'}`}>
                      <div>
                        <div className="flex items-center gap-1.5"><CalendarDaysIcon className="h-4 w-4" /><span className="font-medium">{m.fechaProgramada}</span>{vencida && <span className="text-[10px] text-red-600 font-semibold">VENCIDA</span>}</div>
                        <div className="text-xs text-muted-foreground">{m.rutina || 'Revisión'}{m.frecuenciaValor ? ` · cada ${m.frecuenciaValor} ${m.frecuenciaUnidad}` : ''}</div>
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Tip label="Marcar realizada"><Button size="icon" variant="outline" className="size-8 text-emerald-600 hover:text-emerald-700" onClick={() => markDone(m)}><CheckIcon className="h-4 w-4" /></Button></Tip>
                          <Tip label="Editar"><Button size="icon" variant="outline" className="size-8" onClick={() => openProgEdit(m)}><PencilSquareIcon className="h-4 w-4" /></Button></Tip>
                          <Tip label="Eliminar"><Button size="icon" variant="outline" className="size-8" onClick={() => deleteProg(m)}><TrashIcon className="h-4 w-4" /></Button></Tip>
                        </div>
                      )}
                    </div>
                  ); })}
                </div>
              )}
            </CardContent></Card>
          </div>
        </TabsContent>

      </Tabs>

      {/* Etiqueta */}
      <Dialog open={labelOpen} onOpenChange={o => { if (!o) setLabelOpen(false); }}>
        <DialogContent className="w-[95vw] max-w-sm">
          <DialogHeader><DialogTitle>Etiqueta del equipo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg p-3 flex gap-3 items-center" style={{ background: '#ffffff', color: '#111111', border: '2px solid #111111' }}>
              <div className="flex-1 min-w-0">
                <img src={getLogoUrl()} onError={e => { (e.currentTarget as HTMLImageElement).src = '/rocam-new-logo-2025.png'; }} alt="ROCAM" className="h-6 mb-1 object-contain" />
                <div className="font-mono font-extrabold text-xl" style={{ color: '#111111' }}>{asset.code}</div>
                <div className="text-xs leading-tight" style={{ color: '#111111' }}>{asset.description}{asset.modelo ? ' · ' + asset.modelo : ''}</div>
                <div className="text-[10px] mt-1" style={{ color: '#555555' }}>Sector: {AREA_LABEL[asset.area] || asset.area}{asset.fechaAlta ? ' · Alta: ' + asset.fechaAlta : ''}</div>
              </div>
              {qrUrl && <img src={qrUrl} alt="QR" className="w-28 h-28" />}
            </div>
            <p className="text-xs text-muted-foreground text-center">Escaneá el QR con el celular para ver la ficha y subir la foto del equipo.</p>
            <Button onClick={printLabel} className="w-full gap-1.5"><PrinterIcon className="h-4 w-4" /> Imprimir etiqueta</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AssetFormModal open={editing} editAsset={asset} onClose={() => setEditing(false)} onSaved={load} />

      {/* Programar / Editar revisión */}
      <Dialog open={showProgForm} onOpenChange={o => { if (!o) { setShowProgForm(false); setEditProgId(null); } }}>
        <DialogContent className="w-[95vw] max-w-xl">
          <DialogHeader><DialogTitle>{editProgId ? 'Editar revisión' : 'Programar revisión'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">Fecha</Label><DatePicker value={progForm.fechaProgramada} onChange={v => setProgForm(f => ({ ...f, fechaProgramada: v }))} /></div>
              <div className="space-y-1"><Label className="text-xs">Rutina / tipo</Label>
                <Input value={progForm.rutina} onChange={e => setProgForm(f => ({ ...f, rutina: e.target.value }))} placeholder="Ej. service trimestral..." />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={progForm.recurrente} onChange={e => setProgForm(f => ({ ...f, recurrente: e.target.checked }))} /> Recurrente</label>
            {progForm.recurrente && (
              <div className="flex items-center gap-2 text-sm">cada
                <Input type="number" min={1} className="w-20" value={progForm.frecuenciaValor} onChange={e => setProgForm(f => ({ ...f, frecuenciaValor: e.target.value }))} />
                <Select value={progForm.frecuenciaUnidad} onValueChange={v => setProgForm(f => ({ ...f, frecuenciaUnidad: v }))}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="dias">días</SelectItem><SelectItem value="semanas">semanas</SelectItem><SelectItem value="meses">meses</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1"><Label className="text-xs">Observaciones</Label><Input value={progForm.observaciones} onChange={e => setProgForm(f => ({ ...f, observaciones: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowProgForm(false); setEditProgId(null); }}>Cancelar</Button>
            <Button onClick={submitProg} disabled={saving}>{editProgId ? 'Guardar cambios' : 'Programar revisión'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetDetail;
